import capitalize from 'lodash/capitalize';
import startCase from 'lodash/startCase';
import words from 'lodash/words';
import trim from 'lodash/trim';
import trimStart from 'lodash/trimStart';
import { truncate } from 'lodash';

/**
 * Converts a string to alphabetic case format
 * @param title - The string to be converted
 * @returns The string in alphabetic case format
 * @example
 * toAlphabeticCase('john ronaldo') // returns 'John Ronaldo'
 */
export const toAlphabeticCase = (title: string): string => {
  if (!title) return '';
  return startCase(title.toLowerCase());
};

/**
 * Formats a full name by converting it to alphabetic case
 * @param firstName - The first name of the person
 * @param lastName - The last name of the person
 * @returns The formatted full name
 * @example
 * formatAlphabeticFullName('john', 'ronaldo') // returns 'John Ronaldo'
 */
export const formatAlphabeticFullName = (
  firstName: string,
  lastName: string,
): string => {
  if (!firstName && !lastName) return '';
  return toAlphabeticCase(`${firstName} ${lastName}`.trim());
};

/**
 * Capitalizes the first letter of each word in a string using Lodash
 * @param text - The string to be converted
 * @returns The string with first letter of each word capitalized
 * @example
 * toTitleCase('anudon thapa') // returns 'Anudon Thapa'
 */
export const toTitleCase = (text: string): string => {
  if (!text) return '';
  return words(text.toLowerCase())
    .map((word) => capitalize(word))
    .join(' ');
};

/**
 * Removes leading minus and trims whitespace from a string.
 *
 * @param value - The input string
 * @param trimValue - the removed string, if it is not provided then it will remove the leading minus
 * @returns The sanitized string
 * @example
 * formatString('-Hello ') // 'Hello'
 */
export const formatTrimStart = (value: string, trimValue: string): string => {
  if (!value) return '';
  return trim(trimStart(value, trimValue));
};

export const truncateWithEllipsis = (text: string, maxLength = 50) =>
  truncate(text, {
    length: maxLength + 3,
    omission: '...',
  });
